import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fixturesAPI, seriesAPI, divisionsAPI, teamsAPI, groundsAPI, conditionsAPI } from '../../services/api';
import { PageHeader, DataTable, Spinner, LoadingPage, ConfirmDialog } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const isNoDivisions = s => s === 'no_divisions' || s === 'groups_only';

// ─── Assign Conflict Modal — shows both teams' availability ───────────────────
function AssignConflictModal({ fixture, allFixtures, grounds, onCancel, onSave }) {
  const [date, setDate] = useState('');
  const [groundId, setGroundId] = useState('');
  const [time, setTime] = useState('12:30');

  const isPremier = fixture.division_name === 'Premier';
  // Premier can only play at premier grounds, 12:30 or 16:00. Other divisions can use any ground + any time.
  const premierGroundIds = new Set(grounds.filter(g => g.ground_type === 'premier').map(g => g.id));
  const allowedTimes = isPremier ? ['12:30:00', '16:00:00'] : ['08:45:00', '12:30:00', '16:00:00'];

  // All Sundays in the season (from fixtures)
  const allDates = [...new Set(allFixtures.map(f => String(f.match_date || '').split('T')[0]).filter(Boolean))].sort();
  // Dates where home or away team already plays
  const homeDates = new Set(allFixtures.filter(f => f.id !== fixture.id && f.status === 'confirmed' &&
    (f.home_team_id === fixture.home_team_id || f.away_team_id === fixture.home_team_id)).map(f => String(f.match_date || '').split('T')[0]));
  const awayDates = new Set(allFixtures.filter(f => f.id !== fixture.id && f.status === 'confirmed' &&
    (f.home_team_id === fixture.away_team_id || f.away_team_id === fixture.away_team_id)).map(f => String(f.match_date || '').split('T')[0]));

  // Build used slot map: "date|ground|time" → count
  const usedSlots = {};
  allFixtures.filter(f => f.status === 'confirmed' && f.match_date && f.ground_id).forEach(f => {
    const d = String(f.match_date).split('T')[0];
    const t = String(f.match_time || '').substring(0, 8);
    const k = `${d}|${f.ground_id}|${t}`;
    usedSlots[k] = (usedSlots[k] || 0) + 1;
  });

  // Dates where both teams are free AND at least one valid ground+time slot is free
  const hasFreeSlot = (d) => {
    for (const g of grounds) {
      if (isPremier && !premierGroundIds.has(g.id)) continue;
      for (const t of allowedTimes) {
        const k = `${d}|${g.id}|${t}`;
        if (!usedSlots[k]) return true;
      }
    }
    return false;
  };
  const bothFreeDates = allDates.filter(d => !homeDates.has(d) && !awayDates.has(d) && hasFreeSlot(d));

  const homeName = fixture.home_team_name || fixture.home_short || '—';
  const awayName = fixture.away_team_name || fixture.away_short || '—';

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,.5)',
        display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onCancel}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10,
          padding:20, minWidth:480, maxWidth:560, maxHeight:'80vh', overflow:'auto' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin:'0 0 4px', fontSize:16, fontWeight:800, color:'var(--text)' }}>
          📅 Assign Conflict Date
        </h3>
        <div style={{ fontSize:12, color:'var(--mid)', marginBottom:14 }}>
          {fixture.slot_label || `${homeName} vs ${awayName}`} — {fixture.division_name}
        </div>

        {/* Availability hints */}
        <div style={{ padding:'10px 12px', background:'var(--card)', borderRadius:8, border:'1px solid var(--border)', marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:6 }}>
            📊 Both teams free on ({bothFreeDates.length} dates)
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
            {bothFreeDates.length === 0 ? (
              <span style={{ fontSize:11, color:'var(--red)' }}>No date available where both teams are free</span>
            ) : bothFreeDates.slice(0, 20).map(d => (
              <button key={d} type="button"
                style={{ fontSize:10, padding:'3px 8px', borderRadius:4, cursor:'pointer',
                  background: date === d ? 'var(--accent)' : 'rgba(0,200,150,.08)',
                  color: date === d ? 'white' : 'var(--accent)',
                  border:'1px solid rgba(0,200,150,.3)', fontFamily:"'Roboto Mono',monospace" }}
                onClick={() => setDate(d)}>
                {d.substring(5)}
              </button>
            ))}
          </div>
          {homeDates.size > 0 && (
            <div style={{ fontSize:10, color:'var(--dim)', marginTop:8 }}>
              <strong>{homeName}</strong> plays on: {[...homeDates].sort().map(d => d.substring(5)).join(', ')}
            </div>
          )}
          {awayDates.size > 0 && (
            <div style={{ fontSize:10, color:'var(--dim)', marginTop:4 }}>
              <strong>{awayName}</strong> plays on: {[...awayDates].sort().map(d => d.substring(5)).join(', ')}
            </div>
          )}
        </div>

        {/* Form */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
          <div>
            <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:3 }}>Date</div>
            <input type="date" className="input" style={{ fontSize:12, width:'100%' }}
              value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:3 }}>Time</div>
            <select className="input select" style={{ fontSize:12, width:'100%' }}
              value={time} onChange={e => setTime(e.target.value)}>
              <option value="08:45">8:45 AM</option>
              <option value="12:30">12:30 PM</option>
              <option value="16:00">4:00 PM</option>
            </select>
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:3 }}>Ground</div>
            <select className="input select" style={{ fontSize:12, width:'100%' }}
              value={groundId} onChange={e => setGroundId(e.target.value)}>
              <option value="">Select ground…</option>
              {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary"
            onClick={() => onSave({ date, ground_id: groundId, time })}>
            Save Assignment
          </button>
        </div>
      </div>
    </div>
  );
}

const fmt12 = t => {
  if (!t) return '—';
  const [hStr, mStr] = String(t).split(':');
  const h = parseInt(hStr, 10);
  if (isNaN(h)) return t;
  return `${h % 12 === 0 ? 12 : h % 12}:${mStr||'00'} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = d => {
  if (!d) return '—';
  const [y, m, day] = String(d).split('T')[0].split('-');
  return `${parseInt(m)}/${parseInt(day)}/${y}`;
};

const downloadExcel = async (seriesId, combined = false) => {
  if (!seriesId) return;
  try {
    const token = localStorage.getItem('michca_token');
    const url = combined
      ? `/api/fixtures/export/excel/combined?series_id=${seriesId}`
      : `/api/fixtures/export/excel?series_id=${seriesId}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.error || 'Export failed'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `schedule_${combined ? 'combined_' : ''}${seriesId}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  } catch (err) { toast.error('Download failed: ' + err.message); }
};

const DIV_COLORS = { Premier:'#F5A623', 'Division 1':'#4A90D9', 'Division 2':'#7B68EE', 'Division 3':'#00C896' };
const divColor = name => {
  if (!name) return 'var(--accent)';
  const n = name.toLowerCase();
  if (n.includes('premier'))    return '#F5A623';
  if (n.includes('division 1')) return '#4A90D9';
  if (n.includes('division 2')) return '#7B68EE';
  if (n.includes('division 3')) return '#00C896';
  return 'var(--accent)';
};

// ─── Multi-select pills ───────────────────────────────────────────────────────
function MultiPills({ label, options, selected, onChange, colorFn }) {
  const toggleOption = val => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const allSelected = selected.length === 0;
  return (
    <div>
      <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>{label}</label>
      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
        <button onClick={() => onChange([])}
          style={{ fontSize:11, fontWeight:600, padding:'5px 10px', borderRadius:6, cursor:'pointer', border:'1px solid',
            background:allSelected?'var(--accent)':'var(--surface)', color:allSelected?'white':'var(--mid)',
            borderColor:allSelected?'var(--accent)':'var(--border)', transition:'all .15s' }}>All</button>
        {options.map(opt => {
          const isOn = selected.includes(opt.value);
          const c = colorFn ? colorFn(opt.value) : 'var(--accent)';
          return (
            <button key={opt.value} onClick={() => toggleOption(opt.value)}
              style={{ fontSize:11, fontWeight:600, padding:'5px 10px', borderRadius:6, cursor:'pointer',
                border:`1px solid ${isOn ? c : 'var(--border)'}`,
                background:isOn?`color-mix(in srgb, ${c} 15%, transparent)`:'var(--surface)',
                color:isOn?c:'var(--mid)', transition:'all .15s', display:'flex', alignItems:'center', gap:4 }}>
              {isOn && <span style={{ fontSize:8, fontWeight:900 }}>✓</span>}
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Colorful stat card ───────────────────────────────────────────────────────
function StatCard({ label, value, color, bg, border, icon, sub }) {
  return (
    <div style={{ background:bg, border:`1px solid ${border}`, borderRadius:12, padding:'14px 18px',
        position:'relative', overflow:'hidden', flex:1, minWidth:130 }}>
      <div style={{ position:'absolute', top:10, right:12, fontSize:20, opacity:.25 }}>{icon}</div>
      <div style={{ fontSize:9, color, fontWeight:800, textTransform:'uppercase', letterSpacing:'.6px', marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:30, fontWeight:900, color, fontFamily:"'Roboto Mono',monospace", lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color, opacity:.7, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

// ─── Ground card — shows correct cap/used/unused for active filter ────────────
function GroundCard({ ground, filterMonths, filterGround, allFixtures }) {
  const isPremier = ground.ground_type === 'premier';
  const isFiltered = filterMonths.length > 0 || !!filterGround;
  const dimmed = filterGround && filterGround !== ground.name;

  // Capacity: avDatesByMonth × maxPD, filtered to selected months
  const avByMonth = ground.avDatesByMonth || {};  // { 'YYYY-MM': count }
  const maxPD = ground.maxPD || 1;

  const activeMonths = filterMonths.length > 0 ? filterMonths : Object.keys(avByMonth);
  // C10 exclusive ground: capacity = games_count total (e.g. 3 for Grand Rapids/Toledo)
  // NOT avDates × maxPD — those slots are off-limits for everyone except the exclusive team
  const cap = ground.isExclusive
    ? ground.exclusiveCount || 0
    : activeMonths.reduce((s, m) => s + (avByMonth[m] || 0) * maxPD, 0);

  // Used: games at this ground in active months
  const relevantGames = allFixtures.filter(f => {
    if (f.ground_name !== ground.name) return false;
    if (filterMonths.length > 0) {
      const fm = String(f.match_date||'').split('T')[0].substring(0,7);
      if (!filterMonths.includes(fm)) return false;
    }
    return true;
  });
  const used   = relevantGames.length;
  const unused = Math.max(0, cap - used);
  const pct    = cap > 0 ? Math.round((used / cap) * 100) : 0;

  // Division breakdown
  const byDiv = {};
  relevantGames.forEach(f => { const d = f.division_name||'Unknown'; byDiv[d] = (byDiv[d]||0)+1; });

  return (
    <div style={{
      background:'var(--card)',
      border:`1px solid ${(isFiltered && !dimmed) ? (isPremier ? '#F5A623' : '#4A90D9') : 'var(--border)'}`,
      borderRadius:10, padding:'12px 14px', opacity:dimmed ? 0.3 : 1,
      transition:'opacity .2s, border-color .2s', position:'relative', overflow:'hidden',
      cursor: filterGround ? 'default' : 'pointer',
    }}>
      {/* Top stripe */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background:isPremier?'linear-gradient(90deg,#F5A623,#FF6B35)':'linear-gradient(90deg,#4A90D9,#7B68EE)' }} />

      {/* Header row */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8, marginTop:4 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, color:'var(--text)', lineHeight:1.2 }}>{ground.name}</div>
          <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, marginTop:3, display:'inline-block',
            background:isPremier?'rgba(245,166,35,.15)':'rgba(74,144,217,.15)',
            color:isPremier?'#F5A623':'#4A90D9' }}>
            {isPremier ? '⭐ PREMIER' : '📍 STANDARD'}
          </span>
        </div>
        {/* Big unused number */}
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:22, fontWeight:900, fontFamily:"'Roboto Mono',monospace", lineHeight:1,
            color:unused === 0 ? 'var(--dim)' : unused <= 2 ? 'var(--red)' : 'var(--gold)' }}>
            {unused}
          </div>
          <div style={{ fontSize:9, color:'var(--dim)', fontWeight:700 }}>UNUSED</div>
        </div>
      </div>

      {/* Usage bar */}
      <div style={{ height:4, borderRadius:3, background:'var(--border)', marginBottom:8, overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:3, transition:'width .3s',
          width:`${Math.min(pct, 100)}%`,
          background:pct>=100?'var(--red)':pct>=80?'var(--gold)':'var(--accent)' }} />
      </div>

      {/* Cap / Used row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:8 }}>
        {[
          { label:'Capacity', value:cap,  color:'var(--mid)'   },
          { label:'Used',     value:used, color:'var(--accent)'},
          { label:'Unused',   value:unused, color:unused===0?'var(--dim)':unused<=2?'var(--red)':'var(--gold)' },
        ].map(s => (
          <div key={s.label} style={{ textAlign:'center', padding:'4px 2px',
              background:'var(--surface)', borderRadius:5 }}>
            <div style={{ fontSize:14, fontWeight:800, color:s.color, fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
            <div style={{ fontSize:8, color:'var(--dim)', fontWeight:600, textTransform:'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Division breakdown */}
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
        {Object.entries(byDiv).length === 0 ? (
          <div style={{ fontSize:10, color:'var(--dim)', fontStyle:'italic' }}>No games {filterMonths.length>0 ? 'in selected months' : 'scheduled'}</div>
        ) : (
          Object.entries(byDiv).sort((a,b)=>b[1]-a[1]).map(([div, count]) => (
            <div key={div} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11 }}>
              <span style={{ color:'var(--mid)', display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', flexShrink:0, display:'inline-block',
                  background:DIV_COLORS[div]||'var(--dim)' }} />
                {div}
              </span>
              <span style={{ fontWeight:700, fontFamily:"'Roboto Mono',monospace",
                color:DIV_COLORS[div]||'var(--text)' }}>{count}</span>
            </div>
          ))
        )}
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, paddingTop:4,
            borderTop:'1px solid var(--border)', marginTop:2, color:'var(--dim)' }}>
          <span>{ground.isExclusive
            ? `C10 exclusive · ${ground.exclusiveTeam} only · ${ground.exclusiveCount} games`
            : (() => {
                const datesInPeriod = activeMonths.reduce((s, m) => s + (avByMonth[m]||0), 0);
                const totalDates    = Object.values(avByMonth).reduce((s,v)=>s+v,0);
                const label = filterMonths.length > 0
                  ? `${datesInPeriod} date${datesInPeriod!==1?'s':''} · ${maxPD}/day`
                  : `${totalDates} total dates · ${maxPD}/day`;
                return label;
              })()
          }</span>
          <span style={{ fontFamily:"'Roboto Mono',monospace" }}>{used}/{cap}</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function SchedulePage() {
  const navigate = useNavigate();
  const { isScheduler, user } = useAuthStore();
  const isTeamUser = user?.role === 'team';

  const [series,        setSeries]        = useState([]);
  const [grounds,       setGrounds]       = useState([]);  // includes avDatesByMonth, maxPD
  const [divisions,     setDivisions]     = useState([]);
  const [teams,         setTeams]         = useState([]);
  const [allFixtures,   setAllFixtures]   = useState([]);  // full fixture list for series
  const [stats,         setStats]         = useState({});
  const [loading,       setLoading]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tableView,     setTableView]     = useState('fixtures'); // 'fixtures' | 'slots' | 'health'
  const [slotSearch,    setSlotSearch]    = useState(''); // text search in slot table
  // Inline conflict resolution
  const [conflictOpen,   setConflictOpen]   = useState({}); // fixtureId → bool
  const [conflictDraft,  setConflictDraft]  = useState({}); // fixtureId → {date, ground_id, time}
  const [assignModal,    setAssignModal]    = useState(null); // fixture being assigned
  // Team Health Analyzer
  const [healthChecks, setHealthChecks] = useState({
    doubleBooked:  true,   // 2+ games same day
    longGap:       true,   // gap >= 4 weeks
    veryLongGap:   true,   // gap >= 8 weeks (2 months)
    shortGap:      false,  // gap < 7 days (back-to-back weeks)
    gameCount:     true,   // teams with fewer games than expected
    byeOverload:   false,  // teams with more than 2 consecutive weeks without a game
  });
  const [healthDivFilter, setHealthDivFilter] = useState('');
  const [longGapThreshold,  setLongGapThreshold]  = useState(28);  // days
  const [shortGapThreshold, setShortGapThreshold] = useState(7);   // days

  const [filters, setFilters] = useState({
    series_id:'', team_id:'', ground_id:'', ground_type:'', status:'',
    date_from:'', date_to:'', pool:'', time:'',
    months:[], divisions:[],
  });
  const setFilter = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const currentSeriesStructure = (series||[]).find(s => s.id === filters.series_id)?.structure || 'divisions_pools';
  const scheduleNoDivisions = isNoDivisions(currentSeriesStructure);

  const teamPoolMap = useMemo(() => {
    const m = {}; teams.forEach(t => { if (t.pool) m[t.id] = t.pool; }); return m;
  }, [teams]);

  const availPools = useMemo(() => [...new Set(
    (filters.divisions.length
      ? teams.filter(t => divisions.find(d => d.id===t.division_id && filters.divisions.includes(d.name)))
      : teams).map(t=>t.pool).filter(Boolean)
  )].sort(), [teams, filters.divisions, divisions]);

  const availTimes = useMemo(() =>
    [...new Set(allFixtures.map(f => f.match_time?.substring?.(0,5)).filter(Boolean))].sort(),
  [allFixtures]);

  const availMonthOptions = useMemo(() => {
    const set = new Set(allFixtures.map(f => String(f.match_date||'').split('T')[0].substring(0,7)).filter(Boolean));
    return [...set].sort().map(m => {
      const [y, mo] = m.split('-');
      return { value:m, label:new Date(y, parseInt(mo)-1, 1).toLocaleDateString('en-US', { month:'short', year:'2-digit' }) };
    });
  }, [allFixtures]);

  // ── Client-side filtered fixtures ────────────────────────────────────────
  const filteredFixtures = useMemo(() => allFixtures.filter(f => {
    if (filters.months.length>0) {
      const fm = String(f.match_date||'').split('T')[0].substring(0,7);
      if (!filters.months.includes(fm)) return false;
    }
    if (filters.divisions.length>0 && !filters.divisions.includes(f.division_name)) return false;
    if (filters.pool && teamPoolMap[f.home_team_id]!==filters.pool && teamPoolMap[f.away_team_id]!==filters.pool) return false;
    if (filters.time && f.match_time && !f.match_time.startsWith(filters.time)) return false;
    if (filters.ground_id && f.ground_id !== filters.ground_id && f.ground_name !== grounds.find(g=>g.id===filters.ground_id)?.name) return false;
    if (filters.ground_type) {
      const gnd = grounds.find(g => g.id === f.ground_id || g.name === f.ground_name);
      if (!gnd || gnd.ground_type !== filters.ground_type) return false;
    }
    return true;
  }), [allFixtures, filters.months, filters.divisions, filters.pool, filters.time, filters.ground_id, filters.ground_type, grounds, teamPoolMap]);

  // ── Ground slot table data ─────────────────────────────────────────────────
  // Build one row per ground×date: cap, used, unused, divisions used
  const groundSlotRows = useMemo(() => {
    if (!grounds.length || !allFixtures.length) return [];
    const rows = [];
    grounds.forEach(g => {
      const avByMonth = g.avDatesByMonth || {};
      const maxPD = g.maxPD || 1;
      const allAvDates = g.avDatesAll || []; // sorted date strings
      allAvDates.forEach(date => {
        const month = date.substring(0,7);
        const gamesOnDate = allFixtures.filter(f =>
          f.ground_name === g.name &&
          String(f.match_date||'').split('T')[0] === date
        );
        const used    = gamesOnDate.length;
        // C10 exclusive: capacity per date = 1 (one exclusive home game max per date)
        const slotCap = g.isExclusive ? 1 : maxPD;
        const unused  = Math.max(0, slotCap - used);
        const divs   = [...new Set(gamesOnDate.map(f => f.division_name).filter(Boolean))];
        rows.push({
          ground: g.name, type: g.ground_type||'standard',
          date, month,
          cap: slotCap, used, unused,
          divs,
          gamesDetail: gamesOnDate.map(f => ({
            div: f.division_name, home: f.home_short||f.home_team_name, away: f.away_short||f.away_team_name,
            time: f.match_time?.substring(0,5)
          })),
        });
      });
    });
    return rows.sort((a,b) => a.date.localeCompare(b.date) || a.ground.localeCompare(b.ground));
  }, [grounds, allFixtures]);

  // Team Health Analysis — computed from confirmed fixtures
  const teamHealthData = useMemo(() => {
    const confirmed = allFixtures.filter(f => f.status === 'confirmed');
    if (!confirmed.length) return { issues: [], summary: {} };

    // Build per-team schedule
    const teamMap = {}; // teamId → { short, div, divId, pool, games[] }
    confirmed.forEach(f => {
      const d = String(f.match_date||'').split('T')[0];
      if (!d) return;
      const addGame = (tid, short, div, role) => {
        if (!tid) return;
        if (!teamMap[tid]) teamMap[tid] = { short, div, games: [] };
        teamMap[tid].games.push({ date: d, opponent: role==='home'? f.away_short : f.home_short,
          ground: f.ground_name, fixture: f, role });
      };
      addGame(f.home_team_id, f.home_short, f.division_name, 'home');
      addGame(f.away_team_id, f.away_short, f.division_name, 'away');
    });
    Object.values(teamMap).forEach(t => t.games.sort((a,b) => a.date.localeCompare(b.date)));

    const issues = [];

    Object.entries(teamMap).forEach(([tid, team]) => {
      const div = team.div || '';
      const games = team.games;
      if (!games.length) return;

      // DOUBLE BOOKED: 2+ games same day
      if (healthChecks.doubleBooked) {
        const byDate = {};
        games.forEach(g => { if(!byDate[g.date]) byDate[g.date]=[]; byDate[g.date].push(g); });
        Object.entries(byDate).forEach(([date, gs]) => {
          if (gs.length > 1) issues.push({
            type: 'doubleBooked', severity: 'critical', team: team.short, div,
            date, detail: `${gs.length} games on same day`,
            games: gs.map(g => `${g.role==='home'?'vs':'@'} ${g.opponent} @ ${g.ground}`),
          });
        });
      }

      // GAP ANALYSIS: between consecutive games
      for (let i = 1; i < games.length; i++) {
        const prev = new Date(games[i-1].date + 'T12:00:00Z');
        const curr = new Date(games[i].date   + 'T12:00:00Z');
        const gapDays  = Math.round((curr - prev) / (1000*60*60*24));
        const gapWeeks = +(gapDays / 7).toFixed(1);

        if (healthChecks.veryLongGap && gapDays >= 56) {
          issues.push({ type:'veryLongGap', severity:'high', team:team.short, div,
            from:games[i-1].date, to:games[i].date,
            gapDays, gapWeeks: Math.round(gapWeeks),
            detail:`${Math.round(gapWeeks)} weeks between games (${games[i-1].date} → ${games[i].date})`,
            games:[`Last: vs ${games[i-1].opponent}`, `Next: vs ${games[i].opponent}`],
          });
        } else if (healthChecks.longGap && gapDays >= longGapThreshold && gapDays < 56) {
          issues.push({ type:'longGap', severity:'medium', team:team.short, div,
            from:games[i-1].date, to:games[i].date,
            gapDays, gapWeeks: Math.round(gapWeeks),
            detail:`${Math.round(gapWeeks)} weeks gap (${games[i-1].date} → ${games[i].date})`,
            games:[`Last: vs ${games[i-1].opponent}`, `Next: vs ${games[i].opponent}`],
          });
        }

        if (healthChecks.shortGap && gapDays <= shortGapThreshold) {
          issues.push({ type:'shortGap', severity:'info', team:team.short, div,
            from:games[i-1].date, to:games[i].date,
            gapDays, gapWeeks: Math.round(gapWeeks),
            detail:`Only ${gapDays} days between games`,
            games:[`Game 1: vs ${games[i-1].opponent}`, `Game 2: vs ${games[i].opponent}`],
          });
        }
      }

      // GAME COUNT: flag teams below median
      if (healthChecks.gameCount) {
        const divTeams = Object.values(teamMap).filter(t => t.div === div);
        const counts = divTeams.map(t => t.games.length).sort((a,b)=>a-b);
        const median = counts[Math.floor(counts.length/2)];
        if (games.length < median - 1) {
          issues.push({ type:'gameCount', severity:'medium', team:team.short, div,
            detail:`Only ${games.length} games (division median: ${median})`,
            games: games.map(g => `${g.date}: vs ${g.opponent}`),
          });
        }
      }
    });

    // Overall stats
    const allGaps = [];
    Object.values(teamMap).forEach(team => {
      for (let i=1; i<team.games.length; i++) {
        const d1=new Date(team.games[i-1].date+'T12:00:00Z'), d2=new Date(team.games[i].date+'T12:00:00Z');
        allGaps.push(Math.round((d2-d1)/(1000*60*60*24)));
      }
    });
    allGaps.sort((a,b)=>a-b);
    const summary = {
      totalTeams: Object.keys(teamMap).length,
      minGapDays:  allGaps[0] || 0,
      maxGapDays:  allGaps[allGaps.length-1] || 0,
      medianGapDays: allGaps[Math.floor(allGaps.length/2)] || 0,
      avgGapDays: allGaps.length ? Math.round(allGaps.reduce((s,v)=>s+v,0)/allGaps.length) : 0,
      issueCount: issues.length,
      byType: issues.reduce((a,i)=>{ a[i.type]=(a[i.type]||0)+1; return a; }, {}),
    };
    return { issues, summary, teamMap };
  }, [allFixtures, healthChecks, longGapThreshold, shortGapThreshold]);

  const filteredHealthIssues = useMemo(() => {
    let issues = teamHealthData.issues || [];
    if (healthDivFilter) issues = issues.filter(i => i.div === healthDivFilter);
    return issues.sort((a,b) => {
      const sev = { critical:0, high:1, medium:2, info:3 };
      return (sev[a.severity]||4) - (sev[b.severity]||4);
    });
  }, [teamHealthData, healthDivFilter]);

  // Filtered slot rows
  const filteredSlotRows = useMemo(() => {
    let rows = groundSlotRows;
    if (filters.months.length>0) rows = rows.filter(r => filters.months.includes(r.month));
    if (filters.ground_id) {
      const gName = grounds.find(g=>g.id===filters.ground_id)?.name;
      if (gName) rows = rows.filter(r => r.ground === gName);
    }
    if (slotSearch.trim()) {
      const q = slotSearch.toLowerCase();
      rows = rows.filter(r =>
        r.ground.toLowerCase().includes(q) ||
        r.date.includes(q) ||
        r.divs.some(d => d.toLowerCase().includes(q)) ||
        (q === 'unused' && r.unused > 0) ||
        (q === 'empty' && r.unused > 0) ||
        r.gamesDetail.some(g => g.home?.toLowerCase().includes(q) || g.away?.toLowerCase().includes(q))
      );
    }
    return rows;
  }, [groundSlotRows, filters.months, filters.ground_id, slotSearch, grounds]);

  // ── Loads ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([seriesAPI.list(), groundsAPI.list()]).then(([sRes, gRes]) => {
      const sl = sRes.data || [];
      const gl = gRes.data || gRes || [];
      setSeries(sl); setGrounds(gl);
      if (sl.length) setFilter('series_id', sl[0].id);
    }).catch(() => {});
  }, []);

  // Load ground availability broken down by month
  useEffect(() => {
    if (!filters.series_id || !grounds.length) return;
    const token = localStorage.getItem('michca_token');
    const sid = filters.series_id;

    conditionsAPI.list({ series_id: sid }).then(async condRes => {
      const conds = condRes.data || condRes || [];
      const groundRules   = conds.filter(c => c.condition_type === 'ground_rule');
      const groundBkgs    = conds.filter(c => c.condition_type === 'ground_booking');  // C10

      const updated = await Promise.all(grounds.map(async g => {
        try {
          const av = await fetch(`/api/grounds/${g.id}/availability?series_id=${sid}`,
            { headers: { Authorization: `Bearer ${token}` } }).then(r=>r.json());
          const avDates = (av.availability||av||[])
            .filter(a => a.status==='available')
            .map(a => String(a.available_date).split('T')[0])
            .sort();

          // Group by month
          const avDatesByMonth = {};
          avDates.forEach(d => {
            const m = d.substring(0,7);
            avDatesByMonth[m] = (avDatesByMonth[m]||0)+1;
          });

          const gr    = groundRules.find(r => r.value?.ground_id===g.id);
          const maxPD = gr?.value?.max_games_per_day ? parseInt(gr.value.max_games_per_day) : 1;

          // C10 exclusive ground: capacity = games_count (e.g. 3), NOT avDates × maxPD
          const bk = groundBkgs.find(b => b.value?.ground_id===g.id);
          const isExclusive    = !!bk;
          const exclusiveCount = bk ? (parseInt(bk.value?.games_count)||0) : 0;
          const exclusiveTeam  = bk?.value?.team_short || '';

          return { ...g, avDatesByMonth, avDatesAll:avDates, maxPD, isExclusive, exclusiveCount, exclusiveTeam };
        } catch { return { ...g, avDatesByMonth:{}, avDatesAll:[], maxPD:1, isExclusive:false, exclusiveCount:0, exclusiveTeam:'' }; }
      }));
      setGrounds(updated);
    }).catch(() => {});
  }, [filters.series_id, grounds.length]);

  useEffect(() => {
    if (filters.series_id) {
      divisionsAPI.list(filters.series_id).then(r => setDivisions(r.data||[])).catch(() => {});
      teamsAPI.list({ series_id:filters.series_id }).then(r => setTeams(r.data||[])).catch(() => {});
    } else { setTeams([]); setDivisions([]); }
  }, [filters.series_id]);

  const loadFixtures = async () => {
    setLoading(true);
    try {
      const params = {};
      ['series_id','team_id','ground_id','status','date_from','date_to'].forEach(k => { if (filters[k]) params[k] = filters[k]; });
      if (isTeamUser) params.team_id = user.team_id;
      const [fix, st] = await Promise.all([
        fixturesAPI.list(params),
        fixturesAPI.stats(filters.series_id).catch(() => ({ data:{} })),
      ]);
      setAllFixtures(fix.data || []);
      setStats(st.data || {});
    } catch (err) {
      toast.error('Failed to load: ' + (err?.response?.data?.error || err?.message || ''));
      setAllFixtures([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (filters.series_id) loadFixtures(); }, [filters.series_id, filters.team_id, filters.ground_id, filters.status, filters.date_from, filters.date_to]);

  // ── Conflict resolution helpers ─────────────────────────────────────────────
  const resolveSkipConflict = async (f) => {
    try {
      await fixturesAPI.update(f.id, {
        home_team_id: f.home_team_id, away_team_id: f.away_team_id,
        ground_id: null, match_date: null, match_time: null,
        match_type: f.match_type || 'League', round_number: f.round_number || 0,
        status: 'unscheduled', conflict_notes: 'Skipped by admin',
      });
      toast.success('Marked as unscheduled');
      loadFixtures();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const resolveFlagConflict = async (f) => {
    try {
      await fixturesAPI.update(f.id, {
        home_team_id: f.home_team_id, away_team_id: f.away_team_id,
        ground_id: f.ground_id, match_date: f.match_date, match_time: f.match_time,
        match_type: f.match_type || 'League', round_number: f.round_number || 0,
        status: 'conflict', conflict_notes: 'Flagged for review',
      });
      toast.success('Flagged');
      loadFixtures();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const resolveAssignConflict = async (f, draft) => {
    if (!draft.date || !draft.ground_id) { toast.error('Pick date and ground'); return; }
    try {
      await fixturesAPI.update(f.id, {
        home_team_id: f.home_team_id, away_team_id: f.away_team_id,
        ground_id: draft.ground_id, match_date: draft.date, match_time: draft.time || '12:30',
        match_type: f.match_type || 'League', round_number: f.round_number || 0,
        status: 'confirmed', conflict_notes: null,
      });
      toast.success('Conflict resolved');
      setAssignModal(null);
      loadFixtures();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  // Bulk actions: apply skip/flag to all conflicts in current filter
  const bulkResolveAll = async (mode) => {
    const conflicts = filteredFixtures.filter(f => f.status === 'conflict');
    if (!conflicts.length) { toast('No conflicts to resolve'); return; }
    if (!window.confirm(`${mode === 'skip' ? 'Skip' : 'Flag'} all ${conflicts.length} conflicts?`)) return;
    const fn = mode === 'skip' ? resolveSkipConflict : resolveFlagConflict;
    for (const f of conflicts) { await fn(f); }
    toast.success(`${mode === 'skip' ? 'Skipped' : 'Flagged'} ${conflicts.length}`);
  };

  const handleDeleteSeries = async () => {
    try {
      const token = localStorage.getItem('michca_token');
      await fetch(`/api/fixtures/series/${confirmDelete}`, { method:'DELETE', headers:{Authorization:`Bearer ${token}`} });
      toast.success('Schedule deleted'); setAllFixtures([]); setStats({});
    } catch { toast.error('Delete failed'); }
    finally { setConfirmDelete(null); }
  };

  const statusColor = s => ({ confirmed:'tag-green', conflict:'tag-gold', unscheduled:'tag-purple', cancelled:'tag-red' }[s]||'tag-blue');
  const filterGround = filters.ground_id ? grounds.find(g=>g.id===filters.ground_id)?.name||'' : '';

  // Grounds to show in cards — only those with availability or fixtures
  const groundsForCards = useMemo(() => {
    return grounds.filter(g =>
      Object.keys(g.avDatesByMonth||{}).length > 0 ||
      allFixtures.some(f => f.ground_name === g.name)
    ).sort((a,b) => {
      if (a.ground_type==='premier' && b.ground_type!=='premier') return -1;
      if (b.ground_type==='premier' && a.ground_type!=='premier') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [grounds, allFixtures]);

  const totalCap = useMemo(() => {
    return groundsForCards.reduce((s, g) => {
      if (g.isExclusive) return s + (g.exclusiveCount||0);
      const avByMonth = g.avDatesByMonth || {};
      const months = filters.months.length > 0 ? filters.months : Object.keys(avByMonth);
      return s + months.reduce((ms, m) => ms + (avByMonth[m]||0) * (g.maxPD||1), 0);
    }, 0);
  }, [groundsForCards, filters.months]);

  const totalUnused = useMemo(() => {
    return groundsForCards.reduce((s, g) => {
      const cap = g.isExclusive
        ? (g.exclusiveCount||0)
        : (() => {
            const avByMonth = g.avDatesByMonth || {};
            const months = filters.months.length > 0 ? filters.months : Object.keys(avByMonth);
            return months.reduce((ms, m) => ms + (avByMonth[m]||0) * (g.maxPD||1), 0);
          })();
      const used = allFixtures.filter(f => {
        if (f.ground_name !== g.name) return false;
        if (filters.months.length > 0) {
          const fm = String(f.match_date||'').split('T')[0].substring(0,7);
          if (!filters.months.includes(fm)) return false;
        }
        return true;
      }).length;
      return s + Math.max(0, cap - used);
    }, 0);
  }, [groundsForCards, allFixtures, filters.months]);

  if (loading && !allFixtures.length) return <LoadingPage />;

  return (
    <div style={{ display:'flex', height:'calc(100vh - 56px)', overflow:'hidden' }}>
      <div style={{ flex:1, overflow:'auto', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'18px 20px', flex:1 }}>

          {/* ── Header ── */}
          <PageHeader icon="▦"
            title={isTeamUser ? 'My Schedule' : 'Schedule'}
            subtitle={isTeamUser ? `Fixtures for ${user?.team_name||'your team'}` : 'View, filter and export the full season schedule.'}
            actions={
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {!isTeamUser && (
                  <>
                    <button className="btn btn-ghost" style={{ fontSize:12 }}
                      onClick={() => filters.series_id && downloadExcel(filters.series_id, false)}>📊 By Division</button>
                    <button className="btn btn-ghost" style={{ fontSize:12 }}
                      onClick={() => filters.series_id && downloadExcel(filters.series_id, true)}>📋 All Divisions</button>
                    <button className="btn btn-ghost" style={{ fontSize:12 }}
                      onClick={() => window.location.href='/cricclubs'}>⟳ CricClubs</button>
                  </>
                )}
                {!isTeamUser && isScheduler() && stats.total > 0 && (
                  <button className="btn btn-ghost" style={{ fontSize:12, color:'var(--red)' }}
                    onClick={() => setConfirmDelete(filters.series_id)}>🗑 Delete</button>
                )}
                {!isTeamUser && isScheduler() && (
                  <button className="btn btn-gold" style={{ fontSize:13, fontWeight:700 }}
                    onClick={() => navigate('/schedule/generate')}>⚡ Generate Schedule</button>
                )}
              </div>
            }
          />

          {/* ── Stat cards ── */}
          {stats.total !== undefined && (
            <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
              <StatCard label="Total Fixtures" value={stats.total??'—'} icon="📋"
                color="#00C896" bg="rgba(0,200,150,.08)" border="rgba(0,200,150,.2)"
                sub={filteredFixtures.length !== allFixtures.length ? `${filteredFixtures.length} in filter` : undefined} />
              <StatCard label="Confirmed" value={stats.confirmed??'—'} icon="✅"
                color="#4A90D9" bg="rgba(74,144,217,.08)" border="rgba(74,144,217,.2)"
                sub={stats.total > 0 ? `${Math.round((stats.confirmed/stats.total)*100)}% complete` : undefined} />
              <StatCard label="Conflicts" value={stats.conflicts??'—'} icon="⚠️"
                color={(stats.conflicts||0)>0?'#EF4444':'#00C896'}
                bg={(stats.conflicts||0)>0?'rgba(239,68,68,.08)':'rgba(0,200,150,.08)'}
                border={(stats.conflicts||0)>0?'rgba(239,68,68,.25)':'rgba(0,200,150,.2)'}
                sub={(stats.conflicts||0)>0?'Need resolution':'No conflicts'} />
              <StatCard label="Unused Slots" value={totalUnused} icon="🏟"
                color={totalUnused>10?'#F5A623':'var(--dim)'}
                bg={totalUnused>10?'rgba(245,166,35,.08)':'var(--surface)'}
                border={totalUnused>10?'rgba(245,166,35,.25)':'var(--border)'}
                sub={`of ${totalCap} capacity${filters.months.length>0?' (filtered)':''}`} />
            </div>
          )}

          {/* ── Ground cards ── */}
          {groundsForCards.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'.5px' }}>
                  🏟 Ground Slots
                </div>
                {filters.months.length > 0 && (
                  <span style={{ fontSize:10, color:'var(--gold)', background:'rgba(245,166,35,.1)',
                      padding:'2px 8px', borderRadius:10, border:'1px solid rgba(245,166,35,.2)' }}>
                    📅 {filters.months.length} month{filters.months.length>1?'s':''} selected — capacity = dates × {groundsForCards[0]?.maxPD||3} games/day
                  </span>
                )}
                {filters.months.length === 0 && (
                  <span style={{ fontSize:10, color:'var(--dim)', fontStyle:'italic' }}>
                    Season totals · select a month to see monthly breakdown
                  </span>
                )}
                {totalUnused > 0 && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:10, marginLeft:'auto',
                      color:'var(--gold)', borderColor:'rgba(245,166,35,.4)' }}
                    onClick={() => { setTableView('slots'); setSlotSearch('unused'); }}>
                    ⚠ {totalUnused} unused slots — view in table →
                  </button>
                )}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px, 1fr))', gap:10 }}>
                {groundsForCards.map(g => (
                  <GroundCard key={g.id} ground={g}
                    filterMonths={filters.months}
                    filterGround={filterGround}
                    allFixtures={allFixtures} />
                ))}
              </div>
              {/* Summary bar */}
              {filteredFixtures.length > 0 && (
                <div style={{ marginTop:10, padding:'8px 14px', background:'var(--surface)',
                    borderRadius:8, border:'1px solid var(--border)', display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
                  {['Premier','Division 1','Division 2','Division 3'].map(div => {
                    const count = filteredFixtures.filter(f=>f.division_name===div).length;
                    if (!count) return null;
                    const c = divColor(div);
                    return (
                      <div key={div} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11 }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:c, flexShrink:0 }} />
                        <span style={{ color:'var(--mid)' }}>{div}</span>
                        <span style={{ fontWeight:700, color:c, fontFamily:"'Roboto Mono',monospace" }}>{count}</span>
                      </div>
                    );
                  })}
                  <div style={{ marginLeft:'auto', fontSize:11, color:'var(--dim)' }}>
                    {filteredFixtures.length} fixtures displayed
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Filter bar ── */}
          <div className="card" style={{ padding:'14px 16px', marginBottom:14 }}>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'flex-start' }}>
              {/* Series */}
              <div>
                <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Series</label>
                <select className="input select" style={{ fontSize:12 }} value={filters.series_id}
                  onChange={e => setFilter('series_id', e.target.value)}>
                  {series.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
                </select>
              </div>
              {/* Division multi */}
              {!scheduleNoDivisions && divisions.length > 0 && (
                <MultiPills label="Division"
                  options={divisions.map(d => ({ value:d.name, label:d.name.replace('Division ','Div ') }))}
                  selected={filters.divisions} onChange={v => setFilter('divisions', v)} colorFn={divColor} />
              )}
              {/* Month multi */}
              {availMonthOptions.length > 0 && (
                <MultiPills label="Month"
                  options={availMonthOptions}
                  selected={filters.months} onChange={v => setFilter('months', v)} />
              )}
              {/* Pool */}
              {availPools.length > 0 && (
                <div>
                  <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Pool</label>
                  <select className="input select" style={{ fontSize:12 }} value={filters.pool}
                    onChange={e => setFilter('pool', e.target.value)}>
                    <option value="">All Pools</option>
                    {availPools.map(p => <option key={p} value={p}>Pool {p}</option>)}
                  </select>
                </div>
              )}
              {/* Team */}
              {!isTeamUser && (
                <div>
                  <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Team</label>
                  <select className="input select" style={{ fontSize:12 }} value={filters.team_id}
                    onChange={e => setFilter('team_id', e.target.value)}>
                    <option value="">All Teams</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.short_name}</option>)}
                  </select>
                </div>
              )}
              {/* Ground */}
              <div>
                <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Ground</label>
                <select className="input select" style={{ fontSize:12 }} value={filters.ground_id}
                  onChange={e => setFilter('ground_id', e.target.value)}>
                  <option value="">All Grounds</option>
                  {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              {/* Ground Type */}
              {(() => {
                const types = [...new Set(grounds.map(g => g.ground_type).filter(Boolean))].sort();
                if (types.length === 0) return null;
                return (
                  <div>
                    <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Ground Type</label>
                    <select className="input select" style={{ fontSize:12 }} value={filters.ground_type}
                      onChange={e => setFilter('ground_type', e.target.value)}>
                      <option value="">All Types</option>
                      {types.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                );
              })()}
              {/* Time */}
              {availTimes.length > 0 && (
                <div>
                  <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Time</label>
                  <select className="input select" style={{ fontSize:12 }} value={filters.time}
                    onChange={e => setFilter('time', e.target.value)}>
                    <option value="">All Times</option>
                    {availTimes.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                </div>
              )}
              {/* Status */}
              <div>
                <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>Status</label>
                <select className="input select" style={{ fontSize:12 }} value={filters.status}
                  onChange={e => setFilter('status', e.target.value)}>
                  <option value="">All</option>
                  <option value="confirmed">✅ Confirmed</option>
                  <option value="conflict">⚠ Conflict</option>
                  <option value="unscheduled">📅 Unscheduled</option>
                  <option value="cancelled">❌ Cancelled</option>
                </select>
              </div>
              {/* Dates */}
              <div>
                <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>From</label>
                <input className="input" type="date" style={{ fontSize:11, padding:'6px 7px' }} value={filters.date_from}
                  onChange={e => setFilter('date_from', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize:10, color:'var(--mid)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>To</label>
                <input className="input" type="date" style={{ fontSize:11, padding:'6px 7px' }} value={filters.date_to}
                  onChange={e => setFilter('date_to', e.target.value)} />
              </div>
              {/* Clear */}
              {(filters.divisions.length||filters.months.length||filters.team_id||filters.ground_id||filters.pool||filters.time||filters.status||filters.date_from||filters.date_to) ? (
                <div style={{ alignSelf:'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                    onClick={() => setFilters(p=>({...p, divisions:[],months:[],team_id:'',ground_id:'',ground_type:'',pool:'',time:'',status:'',date_from:'',date_to:''}))}>
                    ✕ Clear all
                  </button>
                </div>
              ) : null}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10 }}>
              <span style={{ fontSize:12, color:'var(--mid)' }}>
                Showing <strong style={{ color:'var(--text)' }}>{filteredFixtures.length}</strong>
                {filteredFixtures.length !== allFixtures.length && (
                  <span style={{ color:'var(--dim)', fontSize:11 }}> (of {allFixtures.length})</span>
                )} matches
              </span>
              {filteredFixtures.filter(f=>f.status==='conflict').length > 0 && (
                <span className="tag tag-gold">{filteredFixtures.filter(f=>f.status==='conflict').length} conflicts</span>
              )}
            </div>
          </div>

          {/* ── View toggle + table ── */}
          <div className="card" style={{ padding:14, marginBottom:14 }}>
            {/* Toggle */}
            <div style={{ display:'flex', gap:6, marginBottom:14, alignItems:'center' }}>
              {[
                { key:'fixtures', label:'📋 Fixtures',    count: filteredFixtures.length },
                { key:'slots',    label:'🏟 Ground Slots', count: filteredSlotRows.length },
                { key:'health',   label:'🩺 Team Health',  count: filteredHealthIssues.length,
                  badgeColor: (teamHealthData.summary?.issueCount||0) > 0 ? 'var(--red)' : 'var(--accent)' },
              ].map(v => (
                <button key={v.key} onClick={() => setTableView(v.key)}
                  style={{ fontSize:12, fontWeight:600, padding:'6px 14px', borderRadius:6, cursor:'pointer',
                    border:'1px solid', transition:'all .15s',
                    background: tableView===v.key ? 'var(--accent)' : 'var(--surface)',
                    color:       tableView===v.key ? 'white'         : 'var(--mid)',
                    borderColor: tableView===v.key ? 'var(--accent)' : 'var(--border)' }}>
                  {v.label}{' '}
                  <span style={{ fontSize:10, opacity:.7,
                    ...(v.badgeColor && v.count > 0 && tableView !== v.key
                      ? { color: v.badgeColor, opacity:1, fontWeight:800 } : {}) }}>
                    ({v.count})
                  </span>
                </button>
              ))}
              {/* Search bar for slot view */}
              {tableView === 'slots' && (
                <div style={{ marginLeft:12, display:'flex', alignItems:'center', gap:8, flex:1, maxWidth:320 }}>
                  <input className="input" style={{ fontSize:12, flex:1 }}
                    placeholder='Search ground, date, division, team… or type "unused"'
                    value={slotSearch} onChange={e => setSlotSearch(e.target.value)} />
                  {slotSearch && (
                    <button className="btn btn-ghost btn-sm" onClick={() => setSlotSearch('')} style={{ fontSize:11 }}>✕</button>
                  )}
                </div>
              )}
              {/* Quick filters for slot view */}
              {tableView === 'slots' && (
                <div style={{ display:'flex', gap:6, marginLeft:8 }}>
                  {['unused','premier','standard'].map(q => (
                    <button key={q} onClick={() => setSlotSearch(slotSearch===q?'':q)}
                      style={{ fontSize:10, fontWeight:600, padding:'4px 10px', borderRadius:6, cursor:'pointer',
                        border:'1px solid', transition:'all .15s',
                        background: slotSearch===q ? 'var(--gold)' : 'var(--surface)',
                        color:       slotSearch===q ? 'white'       : 'var(--mid)',
                        borderColor: slotSearch===q ? 'var(--gold)' : 'var(--border)' }}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {loading ? (
              <div style={{ display:'flex', justifyContent:'center', padding:24 }}><Spinner /></div>
            ) : tableView === 'fixtures' ? (
              <DataTable
                headers={['#','Series','Division','Date','Time','Home','Away','Ground','Status',...(isScheduler()?['Actions']:[])]}
                accentColor="var(--gold)"
                rows={filteredFixtures.map((f,i) => [
                  <span className="mono" style={{ color:'var(--mid)', fontSize:11 }}>{i+1}</span>,
                  <span style={{ fontSize:11 }}>{f.series_short||f.series_name}</span>,
                  <span style={{ fontSize:11, color:divColor(f.division_name) }}>{f.division_name||'—'}</span>,
                  <span className="mono" style={{ fontSize:11 }}>{fmtDate(f.match_date)}</span>,
                  <span className="mono" style={{ fontSize:11 }}>{f.match_time ? fmt12(f.match_time.substring(0,5)) : '—'}</span>,
                  <strong style={{ fontSize:12 }}>{f.home_team_name||f.home_short||f.slot_label?.split(' vs ')?.[0]||'—'}</strong>,
                  <span style={{ fontSize:12 }}>{f.away_team_name||f.away_short||f.slot_label?.split(' vs ')?.[1]||'—'}</span>,
                  <span style={{ fontSize:11 }}>{f.ground_name||'—'}</span>,
                  <span className={`tag ${statusColor(f.status)}`}>{f.status}</span>,
                  ...(isScheduler() ? [
                    f.status === 'conflict' ? (
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-sm" style={{ fontSize:10, background:'rgba(0,200,150,.1)', border:'1px solid rgba(0,200,150,.3)', color:'var(--accent)' }}
                          onClick={() => setAssignModal(f)}>📅 Assign</button>
                        <button className="btn btn-sm" style={{ fontSize:10, background:'rgba(107,114,128,.1)', border:'1px solid rgba(107,114,128,.3)', color:'var(--mid)' }}
                          onClick={() => resolveSkipConflict(f)}>⏭ Skip</button>
                      </div>
                    ) : (
                      <button className="btn btn-ghost btn-sm" onClick={() => toast('Edit fixture — coming soon')}>Edit</button>
                    )
                  ] : [])
                ])}
                emptyMessage={allFixtures.length===0 ? 'No schedule generated yet.' : 'No fixtures match the selected filters.'}
              />
            ) : tableView === 'slots' ? (
              /* Ground Slots table */
              <div>
                <div style={{ fontSize:11, color:'var(--dim)', marginBottom:10, display:'flex', gap:16, flexWrap:'wrap' }}>
                  <span>
                    <strong style={{ color:'var(--text)' }}>{filteredSlotRows.length}</strong> ground×date records
                    {filters.months.length > 0 && <span style={{ color:'var(--gold)', marginLeft:4 }}>({filters.months.length} month{filters.months.length>1?'s':''} selected)</span>}
                  </span>
                  <span>
                    <strong style={{ color:'var(--red)' }}>{filteredSlotRows.filter(r=>r.unused>0).length}</strong> dates with unused slots
                    {slotSearch==='unused' && <span style={{ color:'var(--gold)', marginLeft:4 }}>— filtered</span>}
                  </span>
                  <span>
                    <strong style={{ color:'var(--accent)' }}>
                      {filteredSlotRows.reduce((s,r)=>s+r.unused,0)}
                    </strong> total unused slots
                  </span>
                </div>
                <DataTable
                  headers={['Ground','Type','Date','Month','Avail Dates','Capacity','Used','Unused','Divisions']}
                  accentColor="var(--gold)"
                  rows={filteredSlotRows.map(r => {
                    // Count available dates for this ground in the filtered period
                    const ground = grounds.find(g=>g.name===r.ground);
                    const avByMonth = ground?.avDatesByMonth||{};
                    const periodMonths = filters.months.length>0 ? filters.months : Object.keys(avByMonth);
                    const datesInPeriod = periodMonths.reduce((s,m)=>s+(avByMonth[m]||0),0);
                    const totalUnusedForGround = periodMonths.reduce((s,m)=>s+(avByMonth[m]||0),0) * r.cap
                      - filteredSlotRows.filter(x=>x.ground===r.ground).reduce((s,x)=>s+x.used,0);
                    return [
                      <span style={{ fontWeight:700, fontSize:12 }}>{r.ground}</span>,
                      <span style={{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:8,
                        background:r.type==='premier'?'rgba(245,166,35,.15)':'rgba(74,144,217,.15)',
                        color:r.type==='premier'?'#F5A623':'#4A90D9' }}>{r.type}</span>,
                      <span className="mono" style={{ fontSize:11 }}>{fmtDate(r.date)}</span>,
                      <span style={{ fontSize:10, color:'var(--dim)' }}>
                        {new Date(r.month+'-02').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}
                      </span>,
                      <span style={{ fontFamily:"'Roboto Mono',monospace", fontSize:12, fontWeight:700, color:'var(--blue)' }}>
                        {datesInPeriod}
                        <span style={{ fontSize:9, color:'var(--dim)', marginLeft:3 }}>date{datesInPeriod!==1?'s':''}</span>
                      </span>,
                      <span className="mono" style={{ fontSize:12, color:'var(--mid)' }}>{r.cap}</span>,
                      <span className="mono" style={{ fontSize:12, color:'var(--accent)', fontWeight:700 }}>{r.used}</span>,
                      <span className="mono" style={{ fontSize:14, fontWeight:900,
                        color:r.unused===0?'var(--dim)':r.unused===1?'var(--gold)':'var(--red)' }}>
                        {r.unused}
                        {r.unused>0 && <span style={{ fontSize:9, marginLeft:3, fontWeight:400 }}>slot{r.unused>1?'s':''} free</span>}
                      </span>,
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                        {r.divs.map(d => (
                          <span key={d} style={{ fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:6,
                            background:`color-mix(in srgb, ${divColor(d)} 15%, transparent)`,
                            color:divColor(d) }}>{d.replace('Division ','D')}</span>
                        ))}
                        {r.divs.length===0 && <span style={{ fontSize:10, color:'var(--dim)', fontStyle:'italic' }}>EMPTY</span>}
                      </div>,
                    ];
                  })}
                  emptyMessage={groundSlotRows.length===0 ? 'Load a schedule first.' : 'No slots match search.'}
                />
              </div>
            ) : null}
          </div>

          {/* ── Team Health Analyzer ── */}
          {tableView === 'health' && (
            <div>
              {/* Controls row */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-start', marginBottom:16 }}>

                {/* Check toggles */}
                <div style={{ flex:1, minWidth:320 }}>
                  <div style={{ fontSize:9, fontWeight:800, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:6 }}>
                    Select Checks to Run
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {[
                      { key:'doubleBooked', label:'🔴 Double-booked',   desc:'2 games same day' },
                      { key:'veryLongGap',  label:'🟠 2-month+ gap',    desc:'≥8 weeks between games' },
                      { key:'longGap',      label:'🟡 Long gap',        desc:'≥4 weeks between games' },
                      { key:'gameCount',    label:'🔵 Low game count',  desc:'Below division median' },
                      { key:'shortGap',     label:'⚪ Back-to-back',    desc:'≤7 days apart' },
                    ].map(c => (
                      <button key={c.key}
                        onClick={() => setHealthChecks(prev => ({ ...prev, [c.key]: !prev[c.key] }))}
                        title={c.desc}
                        style={{ fontSize:11, padding:'5px 10px', borderRadius:20, cursor:'pointer',
                          border:'1px solid', transition:'all .15s',
                          background: healthChecks[c.key] ? 'var(--accent)' : 'var(--surface)',
                          color:       healthChecks[c.key] ? 'white'         : 'var(--dim)',
                          borderColor: healthChecks[c.key] ? 'var(--accent)' : 'var(--border)',
                          fontWeight: healthChecks[c.key] ? 700 : 400 }}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Threshold inputs */}
                <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
                  <div>
                    <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:4 }}>Long Gap (days)</div>
                    <input type="number" className="input" min={7} max={120} value={longGapThreshold}
                      style={{ width:72, fontSize:12 }}
                      onChange={e => setLongGapThreshold(parseInt(e.target.value)||28)} />
                  </div>
                  <div>
                    <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:4 }}>Short Gap (days)</div>
                    <input type="number" className="input" min={1} max={14} value={shortGapThreshold}
                      style={{ width:72, fontSize:12 }}
                      onChange={e => setShortGapThreshold(parseInt(e.target.value)||7)} />
                  </div>
                  {/* Division filter */}
                  <div>
                    <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:4 }}>Division</div>
                    <select className="input select" style={{ fontSize:12, width:130 }}
                      value={healthDivFilter} onChange={e => setHealthDivFilter(e.target.value)}>
                      <option value="">All Divisions</option>
                      {divisions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Summary stat bar */}
              {(() => {
                const s = teamHealthData.summary || {};
                const statItems = [
                  { label:'Issues Found', value: filteredHealthIssues.length, color: filteredHealthIssues.length > 0 ? 'var(--red)' : 'var(--accent)' },
                  { label:'Teams Analysed', value: s.totalTeams || 0, color:'var(--mid)' },
                  { label:'Shortest Gap', value: s.minGapDays ? `${s.minGapDays}d` : '—', color:'#F59E0B' },
                  { label:'Longest Gap', value: s.maxGapDays ? `${s.maxGapDays}d` : '—', color: (s.maxGapDays||0)>=56 ? 'var(--red)' : (s.maxGapDays||0)>=28 ? '#F59E0B' : 'var(--accent)' },
                  { label:'Avg Gap', value: s.avgGapDays ? `${s.avgGapDays}d` : '—', color:'var(--mid)' },
                  { label:'Median Gap', value: s.medianGapDays ? `${s.medianGapDays}d` : '—', color:'var(--mid)' },
                ];
                return (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:8, marginBottom:16 }}>
                    {statItems.map(s => (
                      <div key={s.label} style={{ textAlign:'center', padding:'8px 6px',
                          background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)' }}>
                        <div style={{ fontSize:9, color:'var(--dim)', fontWeight:700, textTransform:'uppercase', marginBottom:2 }}>{s.label}</div>
                        <div style={{ fontSize:18, fontWeight:900, color:s.color, fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Issue list */}
              {filteredHealthIssues.length === 0 ? (
                <div style={{ textAlign:'center', padding:'40px 0', color:'var(--dim)', fontSize:13 }}>
                  {allFixtures.filter(f=>f.status==='confirmed').length === 0
                    ? 'No schedule loaded — generate a schedule first'
                    : '✅ No issues found with the selected checks'}
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {filteredHealthIssues.map((issue, idx) => {
                    const sevConfig = {
                      critical: { color:'#EF4444', bg:'rgba(239,68,68,.06)', border:'rgba(239,68,68,.25)', icon:'🔴', label:'CRITICAL' },
                      high:     { color:'#F97316', bg:'rgba(249,115,22,.06)', border:'rgba(249,115,22,.25)', icon:'🟠', label:'HIGH' },
                      medium:   { color:'#F59E0B', bg:'rgba(245,158,11,.06)', border:'rgba(245,158,11,.25)', icon:'🟡', label:'MEDIUM' },
                      info:     { color:'#6B7280', bg:'rgba(107,114,128,.04)', border:'rgba(107,114,128,.2)', icon:'⚪', label:'INFO' },
                    };
                    const sev = sevConfig[issue.severity] || sevConfig.info;
                    const typeLabels = {
                      doubleBooked: 'Double Booked',
                      veryLongGap:  '2-Month+ Gap',
                      longGap:      'Long Gap',
                      shortGap:     'Back-to-Back',
                      gameCount:    'Low Game Count',
                    };
                    return (
                      <div key={idx} style={{ display:'flex', gap:12, padding:'10px 12px', borderRadius:8,
                          background:sev.bg, border:`1px solid ${sev.border}` }}>
                        {/* Left: severity + type */}
                        <div style={{ minWidth:110, flexShrink:0 }}>
                          <div style={{ fontSize:9, fontWeight:800, color:sev.color, textTransform:'uppercase', letterSpacing:'.4px' }}>
                            {sev.icon} {sev.label}
                          </div>
                          <div style={{ fontSize:10, color:'var(--dim)', marginTop:2 }}>{typeLabels[issue.type]||issue.type}</div>
                        </div>
                        {/* Center: team + detail */}
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:'var(--text)' }}>{issue.team}</span>
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:8,
                              background:'rgba(74,144,217,.12)', color:'#4A90D9', fontWeight:700 }}>
                              {issue.div?.replace('Division ','D') || '?'}
                            </span>
                          </div>
                          <div style={{ fontSize:11, color:'var(--mid)', lineHeight:1.5 }}>{issue.detail}</div>
                        </div>
                        {/* Right: related games */}
                        <div style={{ minWidth:180, maxWidth:240 }}>
                          {(issue.games||[]).map((g,i) => (
                            <div key={i} style={{ fontSize:10, color:'var(--dim)', padding:'1px 0',
                                borderBottom: i<issue.games.length-1 ? '1px solid var(--border)' : 'none' }}>
                              {g}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Per-team gap chart — team × week grid */}
              {teamHealthData.teamMap && Object.keys(teamHealthData.teamMap).length > 0 && (() => {
                // Get all unique Sundays in the season
                const allDates = [...new Set(allFixtures.filter(f=>f.status==='confirmed')
                  .map(f=>String(f.match_date||'').split('T')[0]).filter(Boolean))].sort();
                const filteredTeams = Object.entries(teamHealthData.teamMap)
                  .filter(([,t]) => !healthDivFilter || t.div === healthDivFilter)
                  .sort((a,b) => a[1].div.localeCompare(b[1].div) || a[1].short.localeCompare(b[1].short));

                if (!filteredTeams.length) return null;

                const months = [...new Set(allDates.map(d=>d.substring(0,7)))];

                return (
                  <div style={{ marginTop:20 }}>
                    <div style={{ fontSize:10, fontWeight:800, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:8 }}>
                      Team Game Calendar — {filteredTeams.length} teams × {allDates.length} Sundays
                      <span style={{ marginLeft:8, fontWeight:400, textTransform:'none' }}>
                        <span style={{ display:'inline-block', width:10, height:10, borderRadius:2, background:'var(--accent)', marginRight:3 }} />game played
                        <span style={{ display:'inline-block', width:10, height:10, borderRadius:2, background:'rgba(239,68,68,.25)', marginLeft:8, marginRight:3 }} />long gap
                      </span>
                    </div>
                    <div style={{ overflowX:'auto' }}>
                      <div style={{ display:'grid', gridTemplateColumns:`120px repeat(${allDates.length}, 18px)`,
                          gap:'1px', fontSize:8, width:'fit-content', minWidth:'100%' }}>
                        {/* Header row */}
                        <div style={{ fontSize:8, color:'var(--dim)', fontWeight:700 }}>Team</div>
                        {allDates.map(d => {
                          const [,mo,day] = d.split('-');
                          return (
                            <div key={d} style={{ textAlign:'center', color:'var(--dim)', fontWeight:600,
                                writingMode:'vertical-rl', fontSize:7, padding:'2px 0' }}>
                              {mo}/{day}
                            </div>
                          );
                        })}
                        {/* Team rows */}
                        {filteredTeams.map(([tid, team]) => {
                          const gameDateSet = new Set(team.games.map(g=>g.date));
                          // Find long gaps for highlighting
                          const longGapDates = new Set();
                          for (let i=1; i<team.games.length; i++) {
                            const d1=new Date(team.games[i-1].date+'T12:00:00Z');
                            const d2=new Date(team.games[i].date+'T12:00:00Z');
                            const gapDays=Math.round((d2-d1)/(1000*60*60*24));
                            if (gapDays >= longGapThreshold) {
                              // Mark the dates in between as "gap"
                              allDates.filter(d=>d>team.games[i-1].date&&d<team.games[i].date)
                                .forEach(d=>longGapDates.add(d));
                            }
                          }
                          return [
                            <div key={`${tid}-name`} style={{ fontSize:10, fontWeight:700, color:'var(--text)',
                                display:'flex', alignItems:'center', gap:4, overflow:'hidden',
                                whiteSpace:'nowrap', paddingRight:4 }}>
                              <span style={{ fontSize:8, color:'var(--dim)', minWidth:16 }}>
                                {team.div?.replace('Division ','D').replace('Premier','P')}
                              </span>
                              {team.short}
                            </div>,
                            ...allDates.map(d => {
                              const hasGame = gameDateSet.has(d);
                              const isGap   = longGapDates.has(d);
                              return (
                                <div key={`${tid}-${d}`} title={hasGame ? `${team.short} plays ${d}` : isGap ? `Gap for ${team.short}` : ''}
                                  style={{ width:18, height:18, borderRadius:2,
                                    background: hasGame ? 'var(--accent)' : isGap ? 'rgba(239,68,68,.2)' : 'var(--surface)',
                                    border: '1px solid var(--border)' }} />
                              );
                            }),
                          ];
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Conflict list */}
          {false && (
            <div>
              {filteredFixtures.filter(f=>f.status==='conflict').map((f) => {
                const isOpen = !!conflictOpen[f.id];
                const draft  = conflictDraft[f.id] || { date:'', ground_id:'', time:'12:30' };
                const setDraft = (patch) => setConflictDraft(p => ({ ...p, [f.id]: { ...draft, ...patch } }));
                const resolveAssign = async () => {
                  if (!draft.date || !draft.ground_id) { toast.error('Pick date and ground'); return; }
                  try {
                    await fixturesAPI.update(f.id, {
                      home_team_id: f.home_team_id, away_team_id: f.away_team_id,
                      ground_id: draft.ground_id, match_date: draft.date, match_time: draft.time || '12:30',
                      match_type: f.match_type || 'League', round_number: f.round_number || 0,
                      status: 'confirmed', conflict_notes: null,
                    });
                    toast.success('Conflict assigned');
                    setConflictOpen(p => ({ ...p, [f.id]: false }));
                    setConflictDraft(p => { const n = { ...p }; delete n[f.id]; return n; });
                    // Reload fixtures
                    loadFixtures();
                  } catch (err) { toast.error('Failed: ' + (err.response?.data?.error || err.message)); }
                };
                const resolveSkip = async () => {
                  try {
                    await fixturesAPI.update(f.id, {
                      home_team_id: f.home_team_id, away_team_id: f.away_team_id,
                      ground_id: null, match_date: null, match_time: null,
                      match_type: f.match_type || 'League', round_number: f.round_number || 0,
                      status: 'unscheduled', conflict_notes: 'Skipped by admin',
                    });
                    toast.success('Marked as unscheduled');
                    loadFixtures();
                  } catch (err) { toast.error('Failed: ' + (err.response?.data?.error || err.message)); }
                };
                return (
                  <div key={f.id} style={{ marginTop:6, padding:'8px 10px', borderRadius:6,
                      background:'rgba(245,158,11,.06)', border:'1px solid rgba(245,158,11,.2)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, flex:1, minWidth:200 }}>
                        [{f.division_name}] {f.slot_label ? (
                          <strong>{f.slot_label}</strong>
                        ) : (
                          <><strong>{f.home_team_name||f.home_short||'—'}</strong> vs <strong>{f.away_team_name||f.away_short||'—'}</strong></>
                        )}
                        {f.match_type && f.match_type !== 'League' && <span style={{ fontSize:10, marginLeft:6, padding:'1px 6px', borderRadius:4, background:'rgba(155,109,255,.15)', color:'var(--purple)', fontWeight:700 }}>{f.match_type}</span>}
                        {f.conflict_notes && <span style={{ color:'var(--dim)' }}> — {f.conflict_notes}</span>}
                      </span>
                      {isScheduler && (
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-sm" style={{ fontSize:10,
                              background:'rgba(0,200,150,.1)', border:'1px solid rgba(0,200,150,.3)', color:'var(--accent)' }}
                            onClick={() => setConflictOpen(p => ({ ...p, [f.id]: !p[f.id] }))}>
                            {isOpen ? '✕ Cancel' : '📅 Assign'}
                          </button>
                          <button className="btn btn-sm" style={{ fontSize:10,
                              background:'rgba(107,114,128,.1)', border:'1px solid rgba(107,114,128,.3)', color:'var(--mid)' }}
                            onClick={resolveSkip}>
                            ⏭ Skip
                          </button>
                        </div>
                      )}
                    </div>
                    {isOpen && (
                      <div style={{ marginTop:8, display:'grid', gridTemplateColumns:'1fr 1fr 100px auto', gap:6 }}>
                        <input type="date" className="input" style={{ fontSize:11 }}
                          value={draft.date} onChange={e => setDraft({ date: e.target.value })} />
                        <select className="input select" style={{ fontSize:11 }}
                          value={draft.ground_id} onChange={e => setDraft({ ground_id: e.target.value })}>
                          <option value="">Ground…</option>
                          {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                        <select className="input select" style={{ fontSize:11 }}
                          value={draft.time} onChange={e => setDraft({ time: e.target.value })}>
                          <option value="08:45">8:45 AM</option>
                          <option value="12:30">12:30 PM</option>
                          <option value="16:00">4:00 PM</option>
                        </select>
                        <button className="btn btn-primary btn-sm" style={{ fontSize:11 }}
                          onClick={resolveAssign}>Save</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog danger
          message="This will permanently delete ALL fixtures for this series. This action cannot be undone."
          onConfirm={handleDeleteSeries}
          onCancel={() => setConfirmDelete(null)} />
      )}

      {assignModal && (
        <AssignConflictModal
          fixture={assignModal}
          allFixtures={allFixtures}
          grounds={grounds}
          onCancel={() => setAssignModal(null)}
          onSave={(draft) => resolveAssignConflict(assignModal, draft)} />
      )}
    </div>
  );
}